const CONSENT_COOKIE_NAME = 'ccuk_cookie_consent';
const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;
const CONSENT_VERSION = 1;
const CONSENT_CHANGED_EVENT = 'ccuk:cookie-consent-changed';

const PRODUCTION_HOSTS = new Set([
  'appcastingcalls.uk',
  'www.appcastingcalls.uk',
]);

const DEFAULT_GOOGLE_MEASUREMENT_ID = 'G-S6RT452W69';
const DEFAULT_POSTHOG_KEY = 'phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs';
const DEFAULT_POSTHOG_HOST = 'https://us.i.posthog.com';

const BUILD_GA_MEASUREMENT_ID = cleanConfigValue(process.env.REACT_APP_GA_MEASUREMENT_ID);
const BUILD_POSTHOG_KEY = cleanConfigValue(process.env.REACT_APP_POSTHOG_KEY);
const BUILD_POSTHOG_HOST = cleanConfigValue(process.env.REACT_APP_POSTHOG_HOST);
const BUILD_ANALYTICS_DEBUG = cleanConfigValue(process.env.REACT_APP_ANALYTICS_DEBUG);

let googleAnalyticsStarted = false;
let postHogStarted = false;

function cleanConfigValue(value) {
  if (!value || typeof value !== 'string') return '';
  const cleaned = value.trim();
  if (!cleaned || /^%.*%$/.test(cleaned)) return '';
  return cleaned;
}

export function getGoogleMeasurementId() {
  const runtimeValue = typeof window !== 'undefined'
    ? cleanConfigValue(window.__GA_MEASUREMENT_ID__)
    : '';
  const configuredValue = runtimeValue || BUILD_GA_MEASUREMENT_ID || DEFAULT_GOOGLE_MEASUREMENT_ID;
  return /^G-[A-Z0-9]+$/i.test(configuredValue) ? configuredValue.toUpperCase() : '';
}

function getPostHogKey() {
  return BUILD_POSTHOG_KEY || DEFAULT_POSTHOG_KEY;
}

function getPostHogHost() {
  return BUILD_POSTHOG_HOST || DEFAULT_POSTHOG_HOST;
}

function analyticsCanRunHere() {
  if (typeof window === 'undefined') return false;

  const debugEnabled = BUILD_ANALYTICS_DEBUG === 'true';
  return debugEnabled || PRODUCTION_HOSTS.has(window.location.hostname.toLowerCase());
}

function getCookieValue(name) {
  if (typeof document === 'undefined') return '';

  const prefix = `${name}=`;
  const entry = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));

  return entry ? entry.slice(prefix.length) : '';
}

export function getCookieConsent() {
  const rawValue = getCookieValue(CONSENT_COOKIE_NAME);
  if (!rawValue) return null;

  try {
    const decoded = decodeURIComponent(rawValue);

    // Backwards compatibility if an early build stored only a simple string.
    if (decoded === 'accepted') return { analytics: true, version: 0 };
    if (decoded === 'declined') return { analytics: false, version: 0 };

    const parsed = JSON.parse(decoded);
    if (typeof parsed?.analytics !== 'boolean') return null;

    return {
      analytics: parsed.analytics,
      version: Number(parsed.version) || 0,
      updatedAt: parsed.updatedAt || null,
    };
  } catch {
    return null;
  }
}

export function saveCookieConsent(analytics) {
  if (typeof document === 'undefined') return;

  const choice = {
    analytics: Boolean(analytics),
    version: CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
  };
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';

  document.cookie = [
    `${CONSENT_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(choice))}`,
    'Path=/',
    `Max-Age=${CONSENT_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
  ].join('; ') + secure;

  window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: choice }));
}

function injectScript(id, src) {
  if (typeof document === 'undefined' || document.getElementById(id)) return;

  const script = document.createElement('script');
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function initialiseGoogleAnalytics() {
  const measurementId = getGoogleMeasurementId();
  if (!measurementId || !analyticsCanRunHere()) return false;

  window[`ga-disable-${measurementId}`] = false;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };

  // Basic Consent Mode: this code runs only after an explicit opt-in.
  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
  });

  if (!googleAnalyticsStarted) {
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
    injectScript(
      'ccuk-google-analytics',
      `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`,
    );
    googleAnalyticsStarted = true;
  }

  return true;
}

function installPostHogStub() {
  if (window.posthog?.__SV) return window.posthog;

  const posthog = window.posthog || [];
  window.posthog = posthog;

  posthog._i = posthog._i || [];
  posthog.init = function init(token, config, name) {
    function addStub(target, method) {
      const parts = method.split('.');
      let context = target;
      let methodName = method;

      if (parts.length === 2) {
        context = target[parts[0]];
        methodName = parts[1];
      }

      context[methodName] = function stubbedMethod() {
        target.push([method].concat(Array.prototype.slice.call(arguments, 0)));
      };
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.crossOrigin = 'anonymous';
    script.async = true;
    script.src = `${config.api_host.replace('.i.posthog.com', '-assets.i.posthog.com')}/static/array.js`;

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(script, firstScript);

    const instanceName = name || 'posthog';
    const instance = name ? (posthog[name] = []) : posthog;
    instance.people = instance.people || [];
    instance.toString = function toString(compact) {
      let label = 'posthog';
      if (instanceName !== 'posthog') label += `.${instanceName}`;
      return compact ? label : `${label} (stub)`;
    };
    instance.people.toString = function peopleToString() {
      return `${instance.toString(1)}.people (stub)`;
    };

    [
      'capture',
      'identify',
      'reset',
      'opt_in_capturing',
      'opt_out_capturing',
      'has_opted_in_capturing',
      'has_opted_out_capturing',
      'startSessionRecording',
      'stopSessionRecording',
      'sessionRecordingStarted',
      'setPersonProperties',
      'get_distinct_id',
      'get_session_id',
      'onFeatureFlags',
      'isFeatureEnabled',
    ].forEach((method) => addStub(instance, method));

    posthog._i.push([token, config, instanceName]);
  };
  posthog.__SV = 1;

  return posthog;
}

function initialisePostHog() {
  const key = getPostHogKey();
  if (!key || !analyticsCanRunHere()) return false;

  if (window.posthog?.__SV && postHogStarted) {
    window.posthog.opt_in_capturing?.();
    window.posthog.startSessionRecording?.();
    return true;
  }

  const posthog = installPostHogStub();
  posthog.init(key, {
    api_host: getPostHogHost(),
    person_profiles: 'identified_only',
    session_recording: {
      recordCrossOriginIframes: true,
      capturePerformance: false,
    },
  });
  postHogStarted = true;
  return true;
}

function expireCookie(name, domain = '') {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const domainPart = domain ? `; Domain=${domain}` : '';
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax${domainPart}${secure}`;
}

function clearOptionalAnalyticsCookies() {
  if (typeof document === 'undefined') return;

  const names = document.cookie
    .split(';')
    .map((entry) => entry.trim().split('=')[0])
    .filter((name) => (
      name === '_ga'
      || name.startsWith('_ga_')
      || name === '_gid'
      || name === '_gat'
      || name.startsWith('ph_')
    ));

  const hostname = window.location.hostname;
  const domains = ['', hostname, `.${hostname}`];

  names.forEach((name) => {
    domains.forEach((domain) => expireCookie(name, domain));
  });
}

export function startOptionalAnalytics() {
  if (!analyticsCanRunHere()) return;
  initialiseGoogleAnalytics();
  initialisePostHog();
}

export function stopOptionalAnalytics() {
  if (typeof window === 'undefined') return;

  const measurementId = getGoogleMeasurementId();
  if (measurementId) {
    window[`ga-disable-${measurementId}`] = true;
    window.gtag?.('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  }

  window.posthog?.stopSessionRecording?.();
  window.posthog?.reset?.();
  window.posthog?.opt_out_capturing?.();
  clearOptionalAnalyticsCookies();
}

export function applyStoredAnalyticsConsent() {
  const consent = getCookieConsent();

  if (consent?.analytics) startOptionalAnalytics();
  else stopOptionalAnalytics();

  return consent;
}

export { CONSENT_CHANGED_EVENT };
